import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
    LayoutDashboard, CircleDollarSign, BookOpen, Tags, ShoppingBag, Briefcase, Trophy,
    Newspaper, Mail, Settings, LogOut, ExternalLink, Library, Menu, X,
} from "lucide-react";

const links = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/hero-slider", label: "Hero Slider", icon: BookOpen },
    { to: "/admin/books", label: "Books", icon: BookOpen },
    { to: "/admin/categories", label: "Categories", icon: Tags },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
    { to: "/admin/payments", label: "Payments", icon: CircleDollarSign },
    { to: "/admin/libraries", label: "Libraries", icon: Library },
    { to: "/admin/job-circulars", label: "Job Circulars", icon: Briefcase },
    { to: "/admin/winners", label: "Winners", icon: Trophy },
    { to: "/admin/blog", label: "Blog", icon: Newspaper },
    { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
    { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
    const { admin, logout } = useAuth();
    const nav = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const onLogout = async () => {
        await logout();
        nav("/admin/login");
    };

    const closeSidebar = () => setSidebarOpen(false);

    return (
        <div className="min-h-screen flex admin-shell">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/50 md:hidden"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar */}
            <aside className={`admin-side w-64 flex-shrink-0 flex-col fixed inset-y-0 left-0 z-40 transition-transform duration-300 md:static md:flex ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
                <div className="p-5 flex items-center gap-2 border-b border-white/10">
                    <div className="w-9 h-9 rounded-xl bg-white text-brand-navy grid place-items-center font-bold">SP</div>
                    <div className="text-sm font-bold">Self Preparation</div>
                </div>
                <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
                    {links.map((l) => {
                        const Icon = l.icon;
                        return (
                            <NavLink key={l.to} to={l.to} end={l.end} onClick={closeSidebar}
                                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition ${isActive ? "bg-white/15 border-l-4 border-brand-red font-semibold" : "text-white/75 hover:text-white hover:bg-white/10"}`}
                                data-testid={`admin-nav-${l.label.toLowerCase().replace(" ", "-")}`}>
                                <Icon className="w-4 h-4 shrink-0" /> {l.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-white/10 space-y-2">
                    <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-white/65 hover:text-white px-3 py-2"><ExternalLink className="w-3.5 h-3.5" /> View Storefront</Link>
                    <button onClick={onLogout} className="w-full flex items-center gap-2 text-sm text-white/85 hover:text-white px-3 py-2 rounded-lg hover:bg-white/10" data-testid="admin-logout">
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                <header className="bg-white border-b border-brand-light px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-30">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="md:hidden text-brand-navy p-1 -ml-1"
                            aria-label="Open sidebar"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="text-xs text-brand-navy/55">Welcome back,</div>
                            <div className="font-bold text-brand-navy">{admin?.name || admin?.email}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onLogout} className="md:hidden text-sm text-brand-red font-medium">Logout</button>
                    </div>
                </header>
                <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
